import { mapHelper } from "./map-helper";

const LOT_FILL_COLOR: string = "#eeeeee";
const LOT_STROKE_COLOR: string = "#ffffff";

export const enum CanvasOverlayType {
	LotNumber,
	LotOutline,
	Combined,
}

export function createCanvasOverlay(
	map: google.maps.Map,
	layer: google.maps.Data,
	type: CanvasOverlayType = CanvasOverlayType.LotOutline
): google.maps.OverlayView {
	class CanvasOverlay extends google.maps.OverlayView {
		canvas: HTMLCanvasElement | null = null;
		ctx: CanvasRenderingContext2D | null = null;
		features: google.maps.Data.Feature[] | null = null;
		bounds_: google.maps.LatLngBounds | undefined;
		type: CanvasOverlayType = type;
		projection: google.maps.MapCanvasProjection | undefined;

		topRight: google.maps.Point | null = null;
		bottomLeft: google.maps.Point | null = null;

		constructor(public map: google.maps.Map, public layer: google.maps.Data) {
			super();
			this.map = map;
			this.layer = layer;
		}

		onAdd() {
			this.canvas = document.createElement("canvas");
			this.ctx = this.canvas.getContext("2d");
			const panes = this.getPanes();
			panes?.overlayLayer.appendChild(this.canvas);

			this.canvas.style.position = "absolute";
			this.canvas.style.pointerEvents = "none";

			this.bounds_ = mapHelper.getLayerBounds(layer);
		}

		draw() {
			this.setBounds();

			this.setCanvasSize();

			if (this.canvas && this.ctx) {
				this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);

				if (this.type == CanvasOverlayType.LotOutline) {
					this.drawLotOutlines();
				} else if (this.type == CanvasOverlayType.LotNumber) {
					this.drawLotNumbers();
				} else if (this.type == CanvasOverlayType.Combined) {
					this.drawLotOutlines();
					this.drawLotNumbers();
				}
			}
		}

		setBounds() {
			this.bounds_ = mapHelper.getLayerBounds(layer);
			this.projection = this.getProjection();

			this.topRight = this.projection.fromLatLngToDivPixel(
				this.bounds_.getNorthEast()
			);
			this.bottomLeft = this.projection.fromLatLngToDivPixel(
				this.bounds_.getSouthWest()
			);

			if (!this.topRight || !this.bottomLeft) {
				console.error("Error getting topRight or bottomLeft coordinates");
				return;
			}
		}

		setCanvasSize() {
			if (this.topRight && this.bottomLeft) {
				const width = this.topRight.x - this.bottomLeft.x;
				const height = this.bottomLeft.y - this.topRight.y;

				if (this.canvas && this.ctx) {
					this.canvas.width = Math.abs(width);
					this.canvas.height = Math.abs(height);
					this.canvas.style.left = `${this.bottomLeft.x}px`;
					this.canvas.style.top = `${this.topRight.y}px`;
					this.canvas.style.width = `${this.canvas.width}px`;
					this.canvas.style.height = `${this.canvas.height}px`;
					this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
				}
			}
		}

		drawLotNumbers() {
			// this.setBounds();

			// this.setCanvasSize();

			this.layer.forEach((feature: google.maps.Data.Feature) => {
				// const geometry = feature.getGeometry();
				const lot = feature.getProperty("lot") as any;

				const center = mapHelper.getFeaturePosition(feature);
				if (center) {
					const point = this.projection?.fromLatLngToDivPixel(center);
					if (point && this.bottomLeft && this.topRight && this.ctx) {
						this.ctx.fillStyle = "black";
						this.ctx.font = "12px Altform";
						this.ctx.textAlign = "center";
						this.ctx.textBaseline = "middle";
						this.ctx.fillText(
							lot?.number,
							point.x - this.bottomLeft.x,
							point.y - this.topRight.y
						);
					}
				}
			});
		}

		drawLotOutlines() {
			if (this.topRight && this.bottomLeft) {
				const width = this.topRight.x - this.bottomLeft.x;
				const height = this.bottomLeft.y - this.topRight.y;

				if (this.canvas && this.ctx) {
					this.canvas.width = Math.abs(width);
					this.canvas.height = Math.abs(height);

					this.canvas.style.left = `${this.bottomLeft.x}px`;
					this.canvas.style.top = `${this.topRight.y}px`;

					this.canvas.style.width = `${this.canvas.width}px`;
					this.canvas.style.height = `${this.canvas.height}px`;

					// this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
				}

				this.layer.forEach((feature: google.maps.Data.Feature) => {
					const geometry = feature.getGeometry();
					// const lot = feature.getProperty("lot") as any;

					const drawPath = (path: google.maps.LatLng[]) => {
						if (!this.ctx || !this.projection) return;
						this.ctx.beginPath();
						path.forEach((latLng, index) => {
							const point = this.projection?.fromLatLngToDivPixel(latLng);
							if (point && this.ctx && this.bottomLeft && this.topRight) {
								const x = point.x - this.bottomLeft.x;
								const y = point.y - this.topRight.y;
								if (index === 0) {
									this.ctx.moveTo(x, y);
								} else {
									this.ctx.lineTo(x, y);
								}
							}
						});
						this.ctx.closePath();
						this.ctx.fillStyle = LOT_FILL_COLOR;

						this.ctx.fill();
						this.ctx.strokeStyle = LOT_STROKE_COLOR;
						this.ctx.lineWidth = 1;
						this.ctx.stroke();
					};

					if (geometry instanceof google.maps.Data.Polygon) {
						geometry.getArray().forEach((ring) => {
							drawPath(ring.getArray());
						});
					} else if (geometry instanceof google.maps.Data.MultiPolygon) {
						geometry.getArray().forEach((polygon) => {
							polygon.getArray().forEach((ring) => {
								drawPath(ring.getArray());
							});
						});
					}
				});
			}
		}

		updateBounds(bounds: any) {
			this.bounds_ = bounds;
			this.draw();
		}

		// draw() {
		//   if (!this.canvas) return;

		//   const projection = this.getProjection();
		//   const bounds = this.map.getBounds();

		//   if (!bounds || !projection) return;

		//   const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
		//   const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());

		//   if (ne && sw) {
		//     this.canvas.width = Math.abs(ne?.x - sw?.x) / 2;
		//     this.canvas.height = Math.abs(ne?.y - sw?.y) / 2;

		//     console.log(`Drawing canvas ${this.canvas.width} x ${this.canvas.height} at ${sw?.x}, ${ne?.y}`);
		//     console.log(this.canvas.width);

		//     this.canvas.style.left = `${sw?.x}px`;
		//     this.canvas.style.top = `${ne?.y}px`;
		//     this.canvas.style.width = `${this.canvas.width}px`;
		//     this.canvas.style.height = `${this.canvas.height}px`;

		//     if (this.ctx) {
		//       this.ctx.fillStyle = "orange";
		//       this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
		//     }
		//   }
		// }

		onRemove() {
			if (this.canvas && this.canvas.parentNode) {
				this.canvas.parentNode.removeChild(this.canvas);
			}
			this.canvas = null;
		}
	}

	return new CanvasOverlay(map, layer);
}
